import type React from 'react';
import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { api } from 'api/v1/api';
import { AUTH_SESSION_QUERY_KEY } from 'hooks/useAuthSession';
import { useAuthSession } from 'hooks/useAuthSession';
import { useStartOtp } from 'hooks/useOtpChallenge';
import { useNavigate } from 'react-router';
import { addNotification } from 'notifications';
import { Button, TextField } from 'ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import {
  formatEmailDisplay,
  formatPhoneDisplay,
  hasCompleteContacts,
  isEmailComplete,
  isPhoneComplete,
} from './contactUtils';
import classes from './ProfileContactsSection.module.scss';

export const ProfileContactsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const startOtp = useStartOtp(false);
  const { data: session } = useAuthSession();
  const userId = session?.id;

  const sessionPhone = session?.phone ?? '';
  const sessionEmail = session?.email ?? '';
  const phoneVerified = Boolean(session?.phone_verified ?? session?.phone_verified_at);
  const emailVerified = Boolean(session?.email_verified ?? session?.email_verified_at);
  const isComplete = hasCompleteContacts(sessionPhone, sessionEmail);

  const [phone, setPhone] = useState(sessionPhone);
  const [email, setEmail] = useState(sessionEmail);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPhone(sessionPhone);
    setEmail(sessionEmail);
  }, [sessionPhone, sessionEmail]);

  const notifyOtpError = (error: unknown) => {
    const message = getApiErrorMessage(error, 'Временно не работает. Попробуйте позже.');
    setError(message);
    addNotification(message, 'error');
  };

  const handleSave = async () => {
    if (!userId) {
      setError('Не удалось определить пользователя');
      return;
    }

    const payload: Record<string, string> = {};
    if (!isPhoneComplete(sessionPhone)) {
      if (!isPhoneComplete(phone)) {
        setError('Укажите полный номер телефона');
        return;
      }
      payload.phone = phone.trim();
    }
    if (!isEmailComplete(sessionEmail)) {
      const nextEmail = email.trim();
      if (nextEmail && !isEmailComplete(nextEmail)) {
        setError('Укажите корректный email');
        return;
      }
      if (nextEmail) {
        payload.email = nextEmail;
      }
    }

    if (Object.keys(payload).length === 0) {
      setError(null);
      addNotification('Телефон уже сохранен. Почту можно не указывать.', 'success');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await api.put(`/users/${userId}`, payload);
      await queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
      addNotification('Контакты сохранены', 'success');
      if (payload.phone) {
        const started = await startOtp.mutateAsync({
          purpose: 'phone_verify',
          phone: payload.phone,
        });
        const params = new URLSearchParams({
          purpose: 'phone_verify',
          challenge_id: started.challenge_id,
          channel: started.channel || 'vk',
          phone: payload.phone,
        });
        navigate(`/otp?${params.toString()}`);
      }
    } catch (err) {
      notifyOtpError(err);
    } finally {
      setSaving(false);
    }
  };

  if (isComplete) {
    return (
      <section className={classes['contacts-private']} aria-label='Контакты аккаунта'>
        <div className={classes['contacts-private__header']}>
          <span className={classes['contacts-private__eyebrow']}>Только для тебя</span>
          <p className={classes['contacts-private__note']}>
            Телефон и email не показываются в публичном профиле
          </p>
        </div>
        <div className={classes['contacts-private__grid']}>
          <div className={classes['contacts-private__item']}>
            <span className={classes['contacts-private__label']}>Телефон</span>
            <span className={classes['contacts-private__value']}>
              {formatPhoneDisplay(sessionPhone)}
              {phoneVerified ? '' : ' - не подтвержден'}
            </span>
          </div>
          <div className={classes['contacts-private__item']}>
            <span className={classes['contacts-private__label']}>Email</span>
            <span className={classes['contacts-private__value']}>
              {/* Длинная почта в одну строку с многоточием, полный адрес в подсказке. */}
              <span
                className={classes['contacts-private__ellipsis']}
                title={sessionEmail || undefined}
              >
                {formatEmailDisplay(sessionEmail)}
                {sessionEmail ? (emailVerified ? '' : ' - не подтвержден') : ''}
              </span>
            </span>
          </div>
        </div>
        {!phoneVerified && error && (
          <p className={classes['contacts-private__error']}>{error}</p>
        )}
        {!phoneVerified && (
          <Button
            type='button'
            variant='outlined'
            shape='brick'
            size='md'
            loading={startOtp.isPending}
            onClick={() => {
              setError(null);
              startOtp.mutate(
                { purpose: 'phone_verify' },
                {
                  onSuccess: (started) => {
                    const params = new URLSearchParams({
                      purpose: 'phone_verify',
                      challenge_id: started.challenge_id,
                      channel: started.channel || 'vk',
                      ...(sessionPhone ? { phone: sessionPhone } : {}),
                    });
                    navigate(`/otp?${params.toString()}`);
                  },
                  onError: notifyOtpError,
                },
              );
            }}
          >
            Подтвердить телефон
          </Button>
        )}
      </section>
    );
  }

  const showPhoneField = !isPhoneComplete(sessionPhone);
  const showEmailField = !isEmailComplete(sessionEmail);

  return (
    <section className={classes['contacts-block']}>
      <h1 className={classes['contacts-block__title']}>Контакты</h1>
      <p className={classes['contacts-block__hint']}>
        Укажи телефон для связи по заявкам. Почта необязательна. Эти данные видны
        только тебе и собеседнику после обмена контактами в чате.
      </p>

      <div className={classes['contacts-block__fields']}>
        {showPhoneField && (
          <TextField
            shape='brick'
            label='Номер телефона'
            placeholder='+7 (___) ___-__-__'
            mask='ru-phone'
            size='lg'
            value={phone}
            onChange={setPhone}
          />
        )}
        {showEmailField && (
          <TextField
            shape='brick'
            label='Электронная почта (необязательно)'
            placeholder='email@example.com'
            size='lg'
            value={email}
            onChange={setEmail}
          />
        )}
      </div>

      {error && <p className={classes['contacts-block__error']}>{error}</p>}

      <Button
        type='button'
        variant='filled'
        shape='brick'
        color='primary'
        size='md'
        loading={saving}
        disabled={saving}
        onClick={() => void handleSave()}
        className={classes['contacts-block__save']}
      >
        Сохранить контакты
      </Button>
    </section>
  );
};
